import { HttpClient, HttpContext, HttpErrorResponse, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { BaseService } from '@/core/api/base-service';
import { ApiConfiguration } from '@/core/api/api-configuration';
import {
    HistoricoRequest,
    MedicoHistoricoCancelamentoRequest,
    MedicoHistorioCreateResponse,
    MedicoHistorioRemoveRequest,
    MedicoHistorioRequest,
    MedicoHistorioResponse,
    MedicoInscricaoSecundariaRequest
} from '@/core/api/sicom-api/registro/medico/historico/historico.model';
import { Pessoa } from '@/shared/models/pessoa-info.model';
import { Page } from '@/shared/models/page.model';
import { environment } from '@env/environment';
import { SICOM_PATHS } from 'src/sicom-paths';

@Injectable({ providedIn: 'root' })
export class HistoricoService extends BaseService {
    constructor(config: ApiConfiguration, http: HttpClient) {
        super(config, http, environment.apis.apiSicom);
    }
    readonly PATH = SICOM_PATHS.registro.medico.historico;
    readonly PATH_REG = SICOM_PATHS.registro.medico.historicoReg;
    /**
     * Lista todos os registros de histórico.
     * GET /api/v1/cadastro/medico/historico
     * Retorna array de HistoricoRequest.
     */
    list(params: MedicoHistorioRequest, context?: HttpContext): Observable<Page<MedicoHistorioResponse>> {
        let query = new HttpParams();
        if (params?.id != null) query = query.set('id', String(params.id));
        if (params?.uf != null) query = query.set('uf', String(params.uf));
        if (params?.referencia != null) query = query.set('referencia', String(params.referencia));
        if ((params as any)?.page != null) query = query.set('page', String((params as any).page));
        if ((params as any)?.size != null) query = query.set('size', String((params as any).size));
        const sortArr: string[] = (params as any)?.sort || [];
        sortArr.forEach((s) => (query = query.append('sort', s)));
        return this.http.get<Page<MedicoHistorioResponse>>(this.PATH, { params: query, context });
    }

    /**
     * Cria um novo registro de histórico.
     * POST /api/v1/cadastro/medico/historico
     * Retorna true quando criado (201). Para outros erros retorna false.
     */
    create(params: MedicoHistorioCreateResponse, context?: HttpContext): Observable<boolean> {
        return this.http.post(this.PATH_REG, params, { observe: 'response', context }).pipe(
            map((r: HttpResponse<any>) => r.status === 201),
            catchError(() => of(false))
        );
    }

    /**
     * Remove um registro de histórico específico.
     * DELETE /api/v1/cadastro/medico/historico/{id}
     * Retorna true quando removido (204). Para 404 retorna false.
     */
    remove(params: MedicoHistorioRemoveRequest, context?: HttpContext): Observable<boolean> {
        return this.http.request('DELETE', this.PATH_REG, { body: params, observe: 'response', context }).pipe(
            map((r: HttpResponse<any>) => r.status === 204),
            catchError((e: HttpErrorResponse) => {
                if (e.status === 404) return of(false);
                return throwError(() => e);
            })
        );
    }

    buscar(params: Pessoa, context?: HttpContext): Observable<HistoricoRequest[]> {
        const q = new HttpParams().set('id', String(params.id)).set('uf', String(params.uf));
        return this.http.get<HistoricoRequest[]>(`${this.PATH}/consulta`, { params: q, context });
    }

    buscarSecundarias(params: Pessoa, context?: HttpContext): Observable<HistoricoRequest[]> {
        const q = new HttpParams().set('id', String(params?.id ?? '')).set('uf', String(params?.uf ?? ''));
        return this.http.get<HistoricoRequest[]>(`${this.PATH}/consulta-secundarias`, { params: q, context });
    }

    /**
     * Inserir/atualizar data de cancelamento de um registro específico do histórico (MEDICO_SITUACAO).
     * POST /api/v1/cadastro/medico/historico/cancelamento
     * Retorna true quando atualizar retorna 204 (No Content). Para 404 retorna false.
     */
    atualizarCancelamento(params: MedicoHistoricoCancelamentoRequest, context?: HttpContext): Observable<boolean> {
        return this.http.post(`${this.PATH}/cancelamento`, params, { observe: 'response', context }).pipe(
            map((r: HttpResponse<any>) => r.status === 204),
            catchError((e: HttpErrorResponse) => {
                if (e.status === 404) return of(false);
                return throwError(() => e);
            })
        );
    }

    /**
     * Criar uma nova inscrição secundária (ID_MODO_INSCRICAO = 6).
     * POST /api/v1/cadastro/medico/historico/inscricao-secundaria
     * Envia PessoaRequest (query: id, uf) e body com dtInscricao e ufDestino.
     * Retorna true quando criado (201). Para 409 retorna false.
     */
    adicionarInscricaoSecundaria(pessoa: Pessoa, body: MedicoInscricaoSecundariaRequest, context?: HttpContext): Observable<boolean> {
        const q = new HttpParams().set('id', String(pessoa.id)).set('uf', String(pessoa.uf));
        return this.http.post(`${this.PATH}/inscricao-secundaria`, body, { params: q, observe: 'response', context }).pipe(
            map((r: HttpResponse<any>) => r.status === 201),
            catchError((e: HttpErrorResponse) => {
                if (e.status === 409) return of(false);
                return throwError(() => e);
            })
        );
    }
}
